'use client';

import React, { useState } from 'react';

interface Reservation {
  id: string;
  guestName: string;
  guestAvatar: string;
  guestAvatarAlt: string;
  property: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  guests: number;
  total: number;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  bookedOn: string;
  guestEmail?: string;
  guestPhone?: string;
  specialRequests?: string;
  paymentMethod?: string;
}

const reservations: Reservation[] = [
  {
    id: 'RES-001',
    guestName: 'Sarah Nakamura',
    guestAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=60&q=80',
    guestAvatarAlt: 'Sarah Nakamura profile photo, smiling woman with dark hair',
    property: 'Lakeside Villa Entebbe',
    checkIn: '2026-08-25',
    checkOut: '2026-08-30',
    nights: 5,
    guests: 4,
    total: 900000,
    status: 'confirmed',
    bookedOn: '2026-08-10',
    guestEmail: 'sarah.nakamura@email.com',
    guestPhone: '+81 90-1234-5678',
    specialRequests: 'Early check-in if possible. Vegetarian meals preferred.',
    paymentMethod: 'Visa •••• 4242',
  },
  {
    id: 'RES-002',
    guestName: 'James Okonkwo',
    guestAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&q=80',
    guestAvatarAlt: 'James Okonkwo profile photo, man in casual attire',
    property: 'Kampala City Apartment',
    checkIn: '2026-09-01',
    checkOut: '2026-09-04',
    nights: 3,
    guests: 2,
    total: 285000,
    status: 'pending',
    bookedOn: '2026-08-20',
    guestEmail: 'james.okonkwo@email.com',
    guestPhone: '+234 801 234 5678',
    specialRequests: 'Late checkout requested.',
    paymentMethod: 'Mobile Money (MTN)',
  },
  {
    id: 'RES-003',
    guestName: 'Emma Lindqvist',
    guestAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&q=80',
    guestAvatarAlt: 'Emma Lindqvist profile photo, blonde woman smiling outdoors',
    property: 'Bwindi Forest Lodge',
    checkIn: '2026-09-10',
    checkOut: '2026-09-14',
    nights: 4,
    guests: 2,
    total: 1000000,
    status: 'confirmed',
    bookedOn: '2026-08-15',
    guestEmail: 'emma.lindqvist@email.com',
    guestPhone: '+46 70-123 45 67',
    specialRequests: 'Interested in gorilla trekking packages.',
    paymentMethod: 'Mastercard •••• 8810',
  },
  {
    id: 'RES-004',
    guestName: 'David Mwangi',
    guestAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&q=80',
    guestAvatarAlt: 'David Mwangi profile photo, man with glasses',
    property: 'Lakeside Villa Entebbe',
    checkIn: '2026-07-15',
    checkOut: '2026-07-20',
    nights: 5,
    guests: 6,
    total: 900000,
    status: 'completed',
    bookedOn: '2026-07-01',
    guestEmail: 'david.mwangi@email.com',
    guestPhone: '+254 712 345 678',
    specialRequests: 'None',
    paymentMethod: 'Visa •••• 1234',
  },
  {
    id: 'RES-005',
    guestName: 'Amara Diallo',
    guestAvatar: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=60&q=80',
    guestAvatarAlt: 'Amara Diallo profile photo, woman with natural hair',
    property: 'Kampala City Apartment',
    checkIn: '2026-08-05',
    checkOut: '2026-08-07',
    nights: 2,
    guests: 2,
    total: 190000,
    status: 'cancelled',
    bookedOn: '2026-07-28',
    guestEmail: 'amara.diallo@email.com',
    guestPhone: '+221 77 123 45 67',
    specialRequests: 'None',
    paymentMethod: 'Mobile Money (Airtel)',
  },
];

const statusConfig = {
  confirmed: { color: 'bg-emerald-100 text-emerald-700', label: 'Confirmed', dot: 'bg-emerald-500' },
  pending: { color: 'bg-amber-100 text-amber-700', label: 'Pending', dot: 'bg-amber-500' },
  cancelled: { color: 'bg-red-100 text-red-600', label: 'Cancelled', dot: 'bg-red-500' },
  completed: { color: 'bg-blue-100 text-blue-700', label: 'Completed', dot: 'bg-blue-500' },
};

function ReservationDetailModal({ reservation, onClose }: { reservation: Reservation; onClose: () => void }) {
  const cfg = statusConfig[reservation.status];
  const nightly = Math.round(reservation.total / reservation.nights);
  const serviceFee = Math.round(reservation.total * 0.12);
  const subtotal = reservation.total - serviceFee;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-7 pt-7 pb-5 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <span className="text-xs font-bold text-gray-400 tracking-widest uppercase">Reservation</span>
              <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{reservation.id}</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              {reservation.property}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500 text-lg font-bold"
          >
            ×
          </button>
        </div>

        <div className="px-7 py-6 space-y-6">
          {/* Status Banner */}
          <div className={`flex items-center gap-2.5 px-4 py-3 rounded-2xl ${cfg.color} bg-opacity-60`}>
            <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} flex-shrink-0`} />
            <span className="text-sm font-bold">{cfg.label}</span>
            <span className="text-sm opacity-70 ml-auto">Booked on {reservation.bookedOn}</span>
          </div>

          {/* Guest Info */}
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Guest</h3>
            <div className="flex items-center gap-4 bg-gray-50 rounded-2xl p-4">
              <img
                src={reservation.guestAvatar}
                alt={reservation.guestAvatarAlt}
                className="w-14 h-14 rounded-2xl object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-base font-bold text-gray-900">{reservation.guestName}</div>
                {reservation.guestEmail && (
                  <div className="text-sm text-gray-500 mt-0.5 truncate">{reservation.guestEmail}</div>
                )}
                {reservation.guestPhone && (
                  <div className="text-sm text-gray-500">{reservation.guestPhone}</div>
                )}
              </div>
              <button className="text-xs font-semibold text-[#4088C6] border border-[#4088C6]/30 px-3 py-1.5 rounded-xl hover:bg-[#4088C6]/5 transition-colors flex-shrink-0">
                Message
              </button>
            </div>
          </div>

          {/* Stay Details */}
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Stay Details</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="text-xs text-gray-400 font-semibold mb-1">Check-in</div>
                <div className="text-sm font-bold text-gray-900">{reservation.checkIn}</div>
                <div className="text-xs text-gray-400 mt-0.5">From 2:00 PM</div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="text-xs text-gray-400 font-semibold mb-1">Check-out</div>
                <div className="text-sm font-bold text-gray-900">{reservation.checkOut}</div>
                <div className="text-xs text-gray-400 mt-0.5">By 11:00 AM</div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="text-xs text-gray-400 font-semibold mb-1">Guests</div>
                <div className="text-sm font-bold text-gray-900">{reservation.guests} guests</div>
                <div className="text-xs text-gray-400 mt-0.5">{reservation.nights} nights</div>
              </div>
            </div>
          </div>

          {/* Pricing Breakdown */}
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Payment</h3>
            <div className="bg-gray-50 rounded-2xl p-4 space-y-2.5">
              <div className="flex justify-between text-sm text-gray-600">
                <span>UGX {nightly.toLocaleString()} × {reservation.nights} nights</span>
                <span>UGX {subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Service fee (12%)</span>
                <span>UGX {serviceFee.toLocaleString()}</span>
              </div>
              <div className="border-t border-gray-200 pt-2.5 flex justify-between text-sm font-bold text-gray-900">
                <span>Total</span>
                <span>UGX {reservation.total.toLocaleString()}</span>
              </div>
              {reservation.paymentMethod && (
                <div className="flex justify-between text-xs text-gray-400 pt-1">
                  <span>Payment method</span>
                  <span>{reservation.paymentMethod}</span>
                </div>
              )}
            </div>
          </div>

          {/* Special Requests */}
          {reservation.specialRequests && reservation.specialRequests !== 'None' && (
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Special Requests</h3>
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-sm text-amber-800">
                {reservation.specialRequests}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            {reservation.status === 'pending' && (
              <>
                <button className="flex-1 bg-[#4088C6] text-white text-sm font-bold py-3 rounded-2xl hover:bg-[#3070a6] transition-colors">
                  Accept Reservation
                </button>
                <button className="flex-1 bg-gray-100 text-gray-700 text-sm font-bold py-3 rounded-2xl hover:bg-gray-200 transition-colors">
                  Decline
                </button>
              </>
            )}
            {reservation.status === 'confirmed' && (
              <>
                <button className="flex-1 bg-[#4088C6] text-white text-sm font-bold py-3 rounded-2xl hover:bg-[#3070a6] transition-colors">
                  Message Guest
                </button>
                <button className="flex-1 bg-gray-100 text-gray-700 text-sm font-bold py-3 rounded-2xl hover:bg-gray-200 transition-colors">
                  Cancel Reservation
                </button>
              </>
            )}
            {(reservation.status === 'completed' || reservation.status === 'cancelled') && (
              <button className="flex-1 bg-[#4088C6] text-white text-sm font-bold py-3 rounded-2xl hover:bg-[#3070a6] transition-colors">
                Message Guest
              </button>
            )}
            <button
              onClick={onClose}
              className="px-5 bg-gray-100 text-gray-600 text-sm font-bold py-3 rounded-2xl hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Reservations() {
  const [filter, setFilter] = useState<'all' | 'confirmed' | 'pending' | 'completed' | 'cancelled'>('all');
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);

  const filtered = filter === 'all' ? reservations : reservations.filter((r) => r.status === filter);

  const counts = {
    all: reservations.length,
    confirmed: reservations.filter((r) => r.status === 'confirmed').length,
    pending: reservations.filter((r) => r.status === 'pending').length,
    completed: reservations.filter((r) => r.status === 'completed').length,
    cancelled: reservations.filter((r) => r.status === 'cancelled').length,
  };

  return (
    <div>
      {/* Reservation Detail Modal */}
      {selectedReservation && (
        <ReservationDetailModal
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Bookings', value: '5', sub: 'All time' },
          { label: 'Upcoming', value: '3', sub: 'Next 30 days' },
          { label: 'Pending Review', value: '1', sub: 'Needs action' },
          { label: 'Avg Stay', value: '3.8 nights', sub: 'Per booking' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-sm font-semibold text-gray-700 mt-0.5">{s.label}</div>
            <div className="text-xs text-gray-400 mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {(['all', 'confirmed', 'pending', 'completed', 'cancelled'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize whitespace-nowrap transition-all ${
              filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
            }`}
          >
            {f} ({counts[f]})
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Guest</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Property</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Dates</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Guests</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Total</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Status</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <img src={r.guestAvatar} alt={r.guestAvatarAlt} className="w-8 h-8 rounded-full object-cover" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{r.guestName}</div>
                        <div className="text-xs text-gray-400">{r.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700">{r.property}</td>
                  <td className="px-5 py-4">
                    <div className="text-sm text-gray-700">{r.checkIn} → {r.checkOut}</div>
                    <div className="text-xs text-gray-400">{r.nights} nights</div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700">{r.guests}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-gray-900">UGX {r.total.toLocaleString()}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusConfig[r.status].color}`}>
                      {statusConfig[r.status].label}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {r.status === 'pending' ? (
                      <div className="flex gap-2">
                        <button className="text-xs bg-[#4088C6] text-white px-3 py-1 rounded-lg font-semibold hover:bg-[#3070a6] transition-colors">Accept</button>
                        <button className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-lg font-semibold hover:bg-gray-200 transition-colors">Decline</button>
                        <button
                          onClick={() => setSelectedReservation(r)}
                          className="text-xs text-[#4088C6] font-semibold hover:underline"
                        >
                          View
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectedReservation(r)}
                        className="text-xs text-[#4088C6] font-semibold hover:underline"
                      >
                        View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
